import { api } from '@/api/endpoints'
import { queryKeys } from '@/api/queries'
import type { Meta } from '@/api/types'
import { queryClient } from '@/lib/queryClient'
import { installLocalAuth, rememberSession } from './local'
import { initAuth, type AuthSession } from './msal'

export type BootResult = { session: AuthSession; meta: Meta | null }

let bootPromise: Promise<BootResult> | null = null

async function boot(): Promise<BootResult> {
  // /api/meta is public: it tells us whether sign-in is required at all
  // (and, with a local session cookie, who is already signed in).
  const publicMeta = await api.getMeta({ auth: false })
  const session = await initAuth(publicMeta.auth)
  if (session.mode === 'redirecting') return { session, meta: null }
  if (session.mode === 'local') {
    installLocalAuth()
    rememberSession(publicMeta)
  }

  // Signed in with Entra: refetch with the token so `user` reflects the real account.
  const meta = session.mode === 'entra' ? await api.getMeta() : publicMeta
  queryClient.setQueryData(queryKeys.meta, meta)
  return { session, meta }
}

/** Memoised so React StrictMode's double effects don't run MSAL twice. */
export function bootstrap(retry = false): Promise<BootResult> {
  if (!bootPromise || retry) {
    bootPromise = boot().catch((error: unknown) => {
      bootPromise = null
      throw error
    })
  }
  return bootPromise
}
